import { singlePinMutation, singleUnpinMutation } from "api/graphqlTypes";

/**
 * Creates the user's RECORD_USER row for a record, already pinned. Used when
 * the row the UI knows about is missing.
 */
export const createPinnedRecordUserMutation = `
mutation create_record_user($recordId: String!, $userId: String!) {
    create_record_user(body: {record_id: $recordId, user_id: $userId, is_pinned: true}) {
        id
    }
}
`;

export interface PinRecordUserParams {
    /** RECORD_USER row the UI holds for this record; may be missing or stale. */
    recordUserId?: string | null;
    /** The record to pin. */
    recordId?: string | null;
    /** The current user's identity id (RECORD_USER.user_id). */
    userId?: string | null;
    pinned: boolean;
}

type GraphqlPost = (params: { query: string; variables: Record<string, unknown> }) => Promise<unknown>;

/**
 * Pins or unpins a record in the sidebar.
 *
 * A RECORD_USER row exists only while a record is in the user's opened or
 * pinned list, and the backend drops the oldest unpinned rows when more
 * records are opened. So the row id the UI holds can be stale or absent. When
 * pinning, a missing row is created (pinned) instead of failing; when the id
 * is known, the update is tried first and the create is the fallback. If the
 * fallback also fails, the original error is raised.
 *
 * Unpinning always updates the known row: a pinned record keeps its row.
 */
export const pinRecordUser = async (params: PinRecordUserParams, post: GraphqlPost) => {
    const { recordUserId, recordId, userId, pinned } = params;
    const canCreate = pinned && !!recordId && !!userId;
    const create = () =>
        post({ query: createPinnedRecordUserMutation, variables: { recordId, userId } });

    if (!recordUserId) {
        if (!canCreate) {
            throw new Error("Cannot change the pin: the record is not loaded yet.");
        }
        return create();
    }
    try {
        return await post({
            query: pinned ? singlePinMutation : singleUnpinMutation,
            variables: { id: recordUserId },
        });
    } catch (error) {
        if (!canCreate) throw error;
        try {
            return await create();
        } catch {
            throw error;
        }
    }
};
